<template>
  <div class="flex items-center space-x-0.5 text-xs">
    <button
      @click="goToPage(currentPage - 1)"
      :disabled="currentPage === 1"
      class="px-2 py-1 rounded-md hover:bg-white text-white font-bold
      hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed">
      &lt;
    </button>
    <button
      v-if="showFirstPage"
      @click="goToPage(1)"
      :class="['px-2 py-1 rounded-md',
        currentPage === 1 ? 'bg-sky-200 text-gray-900 font-bold cursor-default' :
        'text-white hover:bg-white hover:text-gray-900']">
      1
    </button>
    <span v-if="showFirstEllipsis" class="px-1 text-white">...</span>
    <button
      v-for="page in visiblePages"
      :key="page"
      @click="goToPage(page)"
      :class="['px-2 py-1 rounded-md',
        currentPage === page ? 'bg-sky-200 text-gray-900 font-bold cursor-default' :
        'text-white hover:bg-white hover:text-gray-900']">
      {{ page }}
    </button>
    <span v-if="showLastEllipsis" class="px-1 text-white">...</span>
    <button
      v-if="showLastPage"
      @click="goToPage(totalPages)"
      :class="['px-2 py-1 rounded-md',
        currentPage === totalPages ? 'bg-sky-200 text-gray-900 font-bold cursor-default' :
        'text-white hover:bg-white hover:text-gray-900']">
      {{ totalPages }}
    </button>
    <button
      @click="goToPage(currentPage + 1)"
      :disabled="hasReachedEnd"
      class="px-2 py-1 rounded-md hover:bg-white text-white font-bold
      hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed">
      &gt;
    </button>
    <label for='elementsPerPage' class='flex items-center cursor-not-allowed text-xs'>
      <select
        id='elementsPerPage'
        :value='elementsPerPage'
        @input="$emit('update:elementsPerPage', Number($event.target.value))"
        class='border bg-white hover:bg-gray-100 px-1 py-1 rounded-lg'
        :title="$t('paginationControl.showItemsNumber')">
        <option value='10'>10</option>
        <option value='20'>20</option>
        <option value='50'>50</option>
        <option value='100'>100</option>
      </select>
    </label>
  </div>
</template>

<script>
export default {
  name: 'PageControl',
  props: {
    modelValue: {
      type: Number,
      required: true,
    },
    totalPages: {
      type: Number,
      required: true,
    },
    elementsPerPage: {
      type: Number,
      required: true,
    },
    maxVisible: {
      type: Number,
      default: 5,
    },
  },
  emits: ['update:modelValue'],
  computed: {
    currentPage: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
    hasReachedEnd() {
      return this.currentPage === this.totalPages;
    },
    showFirstPage() {
      return this.visiblePages.length > 0 && !this.visiblePages.includes(1);
    },
    showLastPage() {
      return this.totalPages > 1 && !this.visiblePages.includes(this.totalPages);
    },
    showFirstEllipsis() {
      return this.visiblePages.length > 0 && this.visiblePages[0] > 2;
    },
    showLastEllipsis() {
      return this.visiblePages.length > 0
        && this.visiblePages[this.visiblePages.length - 1] < this.totalPages - 1;
    },
    visiblePages() {
      if (this.totalPages === 1) { return [1]; }

      const pages = [];
      const half = Math.floor(this.maxVisible / 2);

      let start = Math.max(2, this.currentPage - half);
      let end = this.currentPage + half;

      if (this.totalPages !== null) {
        end = Math.min(end, this.totalPages);
      }

      if (this.currentPage - half < 2) {
        end = Math.min(end, this.maxVisible + 1);
      }

      if (this.totalPages !== null && this.currentPage + half > this.totalPages - 1) {
        start = Math.max(2, this.totalPages - this.maxVisible + 1);
      }

      for (let i = start; i <= end; i += 1) {
        pages.push(i);
      }
      return pages;
    },
  },
  methods: {
    goToPage(page) {
      if (page !== this.currentPage) {
        this.currentPage = page;
        this.$emit('update:modelValue', page);
        this.$parent.navigatePages();
        this.emitter.emit('back-on-top', true);
      }
    },
  },
};
</script>
